import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format, startOfDay, endOfDay } from 'date-fns';
import { sortBy } from 'lodash';
import { CardHeader, PseudoStates, useSession } from '@cordata/buffet';
import { all, spread } from 'axios';

import { authorizedPyInstance as axios, CardItemLimit } from '~/utils';

import RemindersForm from '~/components/Reminder/Form';

import ListGroupItemReminders from '~/components/Reminder/ListGroupItem';

const CardReminders = () => {
  const today = new Date();
  const startDate = format(startOfDay(today));
  const endDate = format(endOfDay(today));

  const { currentAppUser } = useSession();
  const [myUserId, setMyUserId] = useState(null);

  const [myReminders, setMyReminders] = useState([]);
  const [unassignedReminders, setUnassignedReminders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState();

  const [didSaveReminder, setDidSaveReminder] = useState(false);
  const [isAddReminderFormOpen, setIsAddReminderFormOpen] = useState(false);

  useEffect(() => {
    if (currentAppUser) {
      setMyUserId(currentAppUser.id);
    }
  }, [currentAppUser]);

  const loadReminders = useCallback(
    () =>
      all([
        axios.get(`/api/data/public/home-page-notifications-view?reminder_date__>=${startDate}&reminder_date__<=${endDate}&target_uid=${myUserId}`),
        axios.get(`/api/data/public/home-page-notifications-view?reminder_date__>=${startDate}&reminder_date__<=${endDate}&target_uid=null`),
      ])
        .then(spread((mine, nobodys) => {
          setMyReminders(mine.data.data.filter(r => !r.attributes.isRead));
          setUnassignedReminders(nobodys.data.data.filter(r => !r.attributes.isRead));
        }))
        .catch(e => setError(e))
        .then(() => setIsLoading(false)),

    [myUserId],
  );

  useEffect(() => {
    loadReminders();
  }, [myUserId]);

  useEffect(() => {
    if (didSaveReminder) {
      setIsAddReminderFormOpen(false);
      setDidSaveReminder(false);

      loadReminders();
    }
  }, [didSaveReminder]);

  const toggleAddReminderForm = () => setIsAddReminderFormOpen(!isAddReminderFormOpen);

  return (
    <Card>
      <CardHeader
        className="text-light"
        color="primary"
        action={
          <Button outline color="light" size="sm" onClick={toggleAddReminderForm}>
            <FontAwesomeIcon icon="plus" />
          </Button>
        }
      >
        Reminders
      </CardHeader>

      <CardBody>
        {!isLoading && !error ? (<>
          <CardTitle>Your Reminders</CardTitle>

          <ListGroup flush>
            {sortBy(myReminders, ['attributes.reminderDate']).reverse().slice(0, CardItemLimit).map(d => (
              <ListGroupItemReminders
                key={d.id}
                data={d}
                setDidSaveReminder={setDidSaveReminder}
              />
            ))}

            {!isLoading && !error && !myReminders.length && (
              <ListGroupItem>
                No reminders assigned to you for today
              </ListGroupItem>
            )}
          </ListGroup>

          <CardTitle className="mt-5">Unassigned Reminders</CardTitle>

          <ListGroup flush>
            {sortBy(unassignedReminders, ['attributes.reminderDate']).reverse().slice(0, CardItemLimit).map(d => (
              <ListGroupItemReminders
                key={d.id}
                data={d}
                setDidSaveReminder={setDidSaveReminder}
              />
            ))}

            {!isLoading && !error && !unassignedReminders.length && (
              <ListGroupItem>
                No unassigned reminders for today
              </ListGroupItem>
            )}

            {isAddReminderFormOpen && (
              <ListGroupItem>
                <span><strong>Add Reminder</strong></span>

                <RemindersForm
                  inline
                  setDidSaveReminder={setDidSaveReminder}
                />
              </ListGroupItem>
            )}
          </ListGroup>
        </>) : (
          <PseudoStates isLoading={isLoading} error={error} />
        )}
      </CardBody>

      <CardFooter className="text-center" tag={Link} to="/home/reminders">
        See All Reminders <FontAwesomeIcon icon="chevron-right" size="sm" />
      </CardFooter>
    </Card>
  );
};

export default CardReminders;
